import type Map from '@/components/map/Map.vue'
import type { LayerSpecification, Map as MaplibreMap, StyleSpecification, SourceSpecification } from 'maplibre-gl'
import { martinTilesBaseUrl } from '@/config/env'
import { apiClient } from '@/plugins/axios/api'
import MapService from '@/services/map/MapService'
import { loadCustomIconImagesForMap } from '@/services/map/mapStyleCustomIcons'
import type { MapLibreStyle } from '@/models/interfaces/map/FondLayerStyle'
import type { TypeOuvrageMarkerConfig } from '@/models/interfaces/map/MarkerConfig'
import { MarkerGeneratorService } from '@/services/map/MarkerGeneratorService'
import { isMarkerConfigEmpty, registerMarkerConfigImage } from '@/services/map/TypeOuvrageMarkerService'

/** Backend style metadata from GET /api/attributs/{id}/style. valueRange only for numeric indicators with data. */
export interface BackendAttributStyleMetadata {
  valueRange?: { min: number; max: number }
  popup?: { attribute?: string; title?: string; format?: string; unit?: string; decimals?: number }
  /** Martin view layer name (e.g. view_cmn_env_rn) derived by backend from atg_geographic_scope + domaine. */
  viewLayer?: string
}

/**
 * Normalize domaine code for Martin view name (same as backend: ENV_RN -> env_rn).
 */
export function normalizeDomaineCodeForView(domaineCode: string | null | undefined): string {
  if (domaineCode == null || domaineCode.trim() === '') return 'env_rn'
  const s = domaineCode
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  return s || 'env_rn'
}

/**
 * Returns Martin view layer name from indicator scope and domaine (mirrors backend getMartinViewLayerByScopeAndDomaine / getMartinViewLayerForIndicator).
 * Use when atgGeographicScope is set; backend is source of truth for style tiles; this is for fallback and data attributes.
 */
export function getMartinViewLayerFromScope(
  internalScope: string | null | undefined,
  domaineCode: string,
  geomTarget?: string | null,
): string {
  const scope = (internalScope ?? '').toLowerCase().trim()
  const suffix = normalizeDomaineCodeForView(domaineCode)

  if (scope === 'infrastructures' && (geomTarget === 'atout' || geomTarget === 'contrainte')) {
    return geomTarget === 'contrainte' ? 'view_contrainte_env_rn' : 'view_atout_env_rn'
  }

  switch (scope) {
    case 'cmn':
      return `view_cmn_${suffix}`
    case 'uad':
      return `view_uad_${suffix}`
    case 'ift':
    case 'infrastructures':
      return `view_infrastructures_${suffix}`
    default:
      return `view_cmn_${suffix}`
  }
}

/**
 * Builds a MapLibre fill-color expression: linear interpolate from valueRange.min to valueRange.max.
 * Uses metadata.popup.attribute as the feature property key (default 'value').
 */
function buildFillColorExpression(
  valueRange: { min: number; max: number },
  attribute: string,
): unknown[] {
  return [
    'interpolate',
    ['linear'],
    ['to-number', ['get', attribute], 0],
    valueRange.min,
    '#f7f7f7',
    valueRange.max,
    '#22c55e',
  ]
}

/**
 * Normalize Martin tile URLs: rewrite https://martin.<domain>/ to <domain>/martin so requests use the same origin as the API (avoids ERR_CONNECTION_CLOSED and CORS).
 */
function normalizeMartinTileUrls(obj: unknown, baseUrl: string): unknown {
  if (typeof obj === 'string') {
    return obj.replace(/https:\/\/martin\.[^/]+\//g, baseUrl.replace(/\/+$/, '') + '/')
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => normalizeMartinTileUrls(item, baseUrl))
  }
  if (obj !== null && typeof obj === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(obj)) {
      out[k] = normalizeMartinTileUrls(v, baseUrl)
    }
    return out
  }
  return obj
}
function applyValueRangeToStyleLayers(
  style: StyleSpecification & { metadata?: BackendAttributStyleMetadata },
): void {
  const valueRange = style.metadata?.valueRange
  if (!valueRange || typeof valueRange.min !== 'number' || typeof valueRange.max !== 'number') {
    return
  }
  const attribute = (style.metadata?.popup?.attribute as string | undefined) ?? 'value'
  const fillColorExpression = buildFillColorExpression(valueRange, attribute)

  if (!style.layers) return
  for (const layer of style.layers) {
    if (layer.type !== 'fill') continue
    const fillLayer = layer as { type: string; paint?: Record<string, unknown> }
    if (!fillLayer.paint) fillLayer.paint = {}
    // Preserve a user-defined interpolate expression already stored in the style JSON
    const existingColor = fillLayer.paint['fill-color']
    if (Array.isArray(existingColor) && existingColor[0] === 'interpolate') continue
    fillLayer.paint['fill-color'] = fillColorExpression
  }
}

/**
 * Service pour gérer les styles MapLibre servis par Martin
 */
export class MartinStyleService {
  private static martinBaseUrl: string = ''
  private static styleCache: globalThis.Map<string, StyleSpecification> = new globalThis.Map()

  /**
   * Initialise le service avec l'URL de base de Martin
   */
  static initialize(martinUrl?: string) {
    this.martinBaseUrl = martinUrl || martinTilesBaseUrl || ''

    if (!this.martinBaseUrl) {
      console.warn(
        "VITE_MARTIN_TILES_URL n'est pas définie. Les styles Martin ne seront pas disponibles.",
      )
      return
    }
  }

  /**
   * Charge un style depuis un fichier JSON local et remplace les variables d'environnement
   */
  static async loadStyleFromFile(styleId: string): Promise<StyleSpecification | null> {
    try {
      // Vérifier le cache
      if (this.styleCache.has(styleId)) {
        return this.styleCache.get(styleId) || null
      }

      // Charger le fichier de style comme texte pour pouvoir remplacer les variables
      const response = await fetch(`/src/styles/map/${styleId}.json`)
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`)
      }

      let styleText = await response.text()

      // Remplacer les variables d'environnement dans le texte
      if (!this.martinBaseUrl) {
        this.initialize()
      }
      const martinUrl = this.martinBaseUrl || ''
      // Remplacer le placeholder par l'URL réelle
      styleText = styleText.replace(/__MARTIN_BASE_URL__/g, martinUrl)

      // Parser le JSON
      const style: StyleSpecification = JSON.parse(styleText)

      // Mettre en cache
      this.styleCache.set(styleId, style)

      return style
    } catch (error) {
      console.error(`Erreur lors du chargement du style ${styleId}:`, error)
      // Essayer avec import si fetch échoue (en développement)
      try {
        const styleModule = await import(`@/styles/map/${styleId}.json`)
        let styleText = JSON.stringify(styleModule.default || styleModule)
        // Remplacer le placeholder
        if (!this.martinBaseUrl) {
          this.initialize()
        }
        const martinUrl = this.martinBaseUrl || ''
        styleText = styleText.replace(/__MARTIN_BASE_URL__/g, martinUrl)
        const style: StyleSpecification = JSON.parse(styleText)
        this.styleCache.set(styleId, style)
        return style
      } catch (importError) {
        console.error(`Erreur lors de l'import du style ${styleId}:`, importError)
        return null
      }
    }
  }

  /**
   * Charge un style depuis l'API Martin
   * Format: GET /style/<style_id>
   */
  static async loadStyleFromMartin(styleId: string): Promise<StyleSpecification | null> {
    if (!this.martinBaseUrl) {
      this.initialize()
    }

    if (!this.martinBaseUrl) {
      console.error("L'URL Martin n'est pas configurée")
      return null
    }

    try {
      // Vérifier le cache
      if (this.styleCache.has(styleId)) {
        return this.styleCache.get(styleId) || null
      }

      // Construire l'URL du style
      const cleanBaseUrl = this.martinBaseUrl.split('?')[0].replace(/\/+$/, '')
      const styleUrl = `${cleanBaseUrl}/style/${styleId}`

      // Charger le style depuis Martin
      const response = await fetch(styleUrl)
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`)
      }

      const style: StyleSpecification = await response.json()

      // Remplacer les variables d'environnement si nécessaire
      const processedStyle = this.replaceEnvironmentVariables(style)

      // Mettre en cache
      this.styleCache.set(styleId, processedStyle)

      return processedStyle
    } catch (error) {
      console.error(`Erreur lors du chargement du style ${styleId} depuis Martin:`, error)
      return null
    }
  }

  /**
   * Loads a map layer style from backend GET /api/map_styles/{styleId}.
   * Use for fond layers and OSM (e.g. osm_subcategory_173). Cached by styleId.
   * @param cmnCode For infra_type_ouvrage_{typId} styles, scopes the returned style's vector
   *   tile URL to a commune perimeter (GET /api/map_styles/{styleId}?cmnCode=...). Bypasses the
   *   cache (both read and write) so a commune-scoped style is never served from/mixed into the
   *   national cache entry for the same styleId, and vice versa.
   * Returns null on 404 or network error.
   */
  static async loadStyleFromMapStyles(styleId: string, cmnCode?: string | null): Promise<StyleSpecification | null> {
    if (!cmnCode && this.styleCache.has(styleId)) {
      return this.styleCache.get(styleId) ?? null
    }
    try {
      const response = await apiClient.get<StyleSpecification>(`/api/map_styles/${styleId}`, {
        params: cmnCode ? { cmnCode } : undefined,
      })
      const style = response.data ?? null
      if (!style) return null
      const processed = this.replaceEnvironmentVariables(style)
      const result = martinTilesBaseUrl
        ? (normalizeMartinTileUrls(processed, martinTilesBaseUrl) as StyleSpecification)
        : processed
      if (!cmnCode) {
        this.styleCache.set(styleId, result)
      }
      return result
    } catch {
      return null
    }
  }

  /**
   * Loads style from backend API GET /api/attributs/{id}/style.
   * Backend returns style with __MARTIN_BASE_URL__ already replaced and indicator filter injected.
   * Tile URLs in form https://martin.<domain>/ are normalized to <domain>/martin to avoid CORS and connection errors.
   * Not cached so that backend updates (e.g. layer filter) are always applied.
   * Returns null on 404 or network error.
   */
  static async loadStyleFromBackend(
    attributId: number | string,
  ): Promise<StyleSpecification | null> {
    try {
      const response = await apiClient.get<StyleSpecification>(`/api/attributs/${attributId}/style`)
      let style = response.data ?? null
      if (style && martinTilesBaseUrl) {
        style = normalizeMartinTileUrls(style, martinTilesBaseUrl) as StyleSpecification
      }
      return style
    } catch {
      return null
    }
  }

  /**
   * Remplace les variables d'environnement dans un style
   */
  private static replaceEnvironmentVariables(style: StyleSpecification): StyleSpecification {
    if (!this.martinBaseUrl) {
      this.initialize()
    }

    const martinUrl = this.martinBaseUrl || ''
    let styleString = JSON.stringify(style)
    styleString = styleString.replace(/__MARTIN_BASE_URL__/g, martinUrl)
    styleString = styleString.replace(/https:\/\/martin\.[^/]+/g, martinUrl.replace(/\/+$/, ''))
    return JSON.parse(styleString) as StyleSpecification
  }

  /**
   * Applique un style à la carte en ajoutant ses sources et layers
   * Préserve les layers persistants existants
   */
  static async applyStyleToMap(
    myMap: InstanceType<typeof Map> | undefined,
    styleId: string,
    loadFromMartin: boolean = false,
  ): Promise<boolean> {
    if (!myMap?.map) {
      console.error("La carte n'est pas initialisée")
      return false
    }

    const map = myMap.map

    // Attendre que la carte soit prête
    if (!map.loaded()) {
      await new Promise<void>((resolve) => {
        map.once('load', () => resolve())
      })
    }

    // Charger le style
    const style = loadFromMartin
      ? await this.loadStyleFromMartin(styleId)
      : await this.loadStyleFromFile(styleId)

    if (!style) {
      console.error(`Impossible de charger le style ${styleId}`)
      return false
    }

    // Récupérer les sources persistants existants (pour référence future si nécessaire)
    const currentStyle = map.getStyle()
    const persistentSources: Record<string, SourceSpecification> = {}

    Object.keys(currentStyle.sources).forEach((key: string) => {
      const source = currentStyle.sources[key] as SourceSpecification
      // Ignorer les sources du basemap (qui ont une URL)
      if (source && 'url' in source === false) {
        persistentSources[key] = source
      }
    })

    // Ajouter les sources du style
    if (style.sources) {
      Object.keys(style.sources).forEach((sourceId) => {
        const source = style.sources![sourceId]
        if (source && !map.getSource(sourceId)) {
          try {
            map.addSource(sourceId, source)
          } catch (error) {
            console.warn(`La source ${sourceId} existe déjà ou ne peut pas être ajoutée:`, error)
          }
        }
      })
    }

    // Ajouter les layers du style
    if (style.layers) {
      style.layers.forEach((layer) => {
        if (!map.getLayer(layer.id)) {
          try {
            map.addLayer(layer)
          } catch (error) {
            console.warn(`Le layer ${layer.id} ne peut pas être ajouté:`, error)
          }
        }
      })
    }

    return true
  }

  /**
   * Waits until Map.vue exposes the maplibre map (after map-ready). Callers often run before `map` is set.
   */
  private static async waitForMaplibreMap(
    myMap: InstanceType<typeof Map> | undefined,
    maxWaitMs: number,
  ): Promise<MaplibreMap | null> {
    if (!myMap) return null
    const pollMs = 50
    let waited = 0
    while (!myMap.map && waited < maxWaitMs) {
      await new Promise((resolve) => setTimeout(resolve, pollMs))
      waited += pollMs
    }
    return myMap.map ?? null
  }

  /**
   * Options when applying a style to the map.
   * - useStyleAsIs: when true, do not run applyValueRangeToStyleLayers so the API style (paint expressions, keys) is used as-is.
   * - layerGroup: metadata.layerGroup to set on added layers (e.g. MapService.LAYER_GROUP.INDICATOR for indicator styles).
   */
  static async applyStyleObjectToMap(
    myMap: InstanceType<typeof Map> | undefined,
    styleObject: StyleSpecification,
    options?: { useStyleAsIs?: boolean; layerGroup?: string },
  ): Promise<boolean> {
    const MAP_INIT_WAIT_MS = 15_000
    const map = await this.waitForMaplibreMap(myMap, MAP_INIT_WAIT_MS)
    if (!map) {
      console.warn('[map-styles] applyStyleObjectToMap: map not initialised (timeout or missing component)')
      return false
    }

    if (!map.loaded()) {
      const LOAD_TIMEOUT_MS = 3000
      await new Promise<void>((resolve) => {
        const timeout = setTimeout(() => {
          console.warn('[map-styles] applyStyleObjectToMap: map wait timeout, proceeding anyway')
          resolve()
        }, LOAD_TIMEOUT_MS)
        map.once('idle', () => {
          clearTimeout(timeout)
          resolve()
        })
      })
    }

    const style = this.replaceEnvironmentVariables(styleObject) as StyleSpecification & {
      metadata?: BackendAttributStyleMetadata
    }
    if (!options?.useStyleAsIs) {
      applyValueRangeToStyleLayers(style)
    }

    if (style.sources) {
      Object.keys(style.sources).forEach((sourceId) => {
        const source = style.sources![sourceId]
        if (!source || map.getSource(sourceId)) return
        try {
          const plainSource = JSON.parse(JSON.stringify(source)) as SourceSpecification
          map.addSource(sourceId, plainSource)
        } catch (error) {
          console.error(`[map-styles] addSource failed for "${sourceId}":`, error)
        }
      })
    }

    const layerGroup =
      options?.layerGroup ?? MapService.LAYER_GROUP.FOND
    if (style.layers) {
      const beforeId = MapService.getFirstIndicatorLayerId(map) ?? undefined
      style.layers.forEach((layer) => {
        if (map.getLayer(layer.id)) return
        try {
          const plainLayer = JSON.parse(JSON.stringify(layer)) as LayerSpecification & {
            metadata?: Record<string, unknown>
          }
          const previousMeta =
            plainLayer.metadata != null && typeof plainLayer.metadata === 'object'
              ? { ...plainLayer.metadata }
              : {}
          plainLayer.metadata = { ...previousMeta, isPersistent: true, layerGroup }
          // Sanitize icon-image: MapLibre requires a non-empty resolvedImage string or expression array.
          // null, "null" (stringified null from JSON), "" and numeric values all cause addLayer to throw
          // or emit a "styleimagemissing" warning with id "null".
          if (plainLayer.type === 'symbol') {
            const layout = plainLayer.layout as Record<string, unknown> | undefined
            if (layout) {
              const iconImage = layout['icon-image']
              const isInvalidIconImage =
                iconImage !== undefined &&
                (iconImage === null ||
                  iconImage === 'null' ||
                  iconImage === '' ||
                  (typeof iconImage !== 'string' && !Array.isArray(iconImage)))
              if (isInvalidIconImage) {
                delete layout['icon-image']
              }
            }
          }
          map.addLayer(plainLayer, beforeId)
        } catch (error) {
          console.error(`[map-styles] addLayer failed for "${layer.id}":`, error)
        }
      })
    }

    return true
  }

  /**
   * Returns source and layer ids from an already-prefixed style (for removal tracking).
   */
  static getAppliedOsmStyleIds(prefixedStyle: StyleSpecification): {
    sourceIds: string[]
    layerIds: string[]
  } {
    const sourceIds = Object.keys(prefixedStyle.sources ?? {})
    const layerIds = (prefixedStyle.layers ?? []).map((layer) => layer.id)
    return { sourceIds, layerIds }
  }

  /**
   * Clones a MapLibre style with all source and layer ids prefixed (prefix + '__' + id).
   * Used to apply multiple OSM styles without id clashes.
   */
  static cloneStyleWithPrefix(style: StyleSpecification, prefix: string): StyleSpecification {
    const sep = '__'
    const sources: Record<string, SourceSpecification> = {}
    for (const [sourceId, source] of Object.entries(style.sources ?? {})) {
      sources[prefix + sep + sourceId] = JSON.parse(JSON.stringify(source)) as SourceSpecification
    }
    const layers = (style.layers ?? []).map((layer) => {
      const plain = JSON.parse(JSON.stringify(layer)) as LayerSpecification & { source?: string }
      plain.id = prefix + sep + layer.id
      if (plain.source) plain.source = prefix + sep + plain.source
      return plain
    })
    return {
      ...style,
      sources,
      layers,
    }
  }

  /** Fallback pin color when a circle layer's circle-color isn't a plain string (e.g. a data-driven expression). */
  private static readonly OSM_PICTO_PIN_FALLBACK_COLOR = '#3aa2bc'

  /**
   * Replaces flat 'circle' point layers with pin-shaped picto markers — same visual language as
   * indicator markers (MarkerGeneratorService) — so OSM/typeOuvrage points read as pins instead of
   * plain dots. Mutates the style's layers in place; falls back to a default color when
   * circle-color is a data-driven expression rather than a plain string (can't rasterize that
   * into a static pin image).
   *
   * When a non-empty marker_config is supplied (per type d'ouvrage, from the atlas_categories
   * response — same value already used by the right panel swatch), the pin is built from it
   * (lucide icon / uploaded icon URL + colors) instead of the flat color-only pin. Types with an
   * empty marker_config keep today's flat pin, unchanged.
   */
  private static async convertCirclePointLayersToPictoPins(
    map: MaplibreMap,
    style: StyleSpecification,
    markerConfig?: TypeOuvrageMarkerConfig | null,
  ): Promise<void> {
    if (!style.layers) return
    const hasCustomMarker = !isMarkerConfigEmpty(markerConfig)

    for (const layer of style.layers) {
      if (layer.type !== 'circle') continue
      const circlePaint = (layer as { paint?: Record<string, unknown> }).paint
      const rawColor = circlePaint?.['circle-color']
      const color = typeof rawColor === 'string' ? rawColor : this.OSM_PICTO_PIN_FALLBACK_COLOR

      let imageId: string | null = null
      // 'bottom' for our own teardrop pin (pointed tip on the ground point); 'center' for a
      // standalone marker_config icon badge (no pointed tip, so the point sits at its center).
      let iconAnchor: 'bottom' | 'center' = 'bottom'
      let resolvedColor = color

      if (hasCustomMarker) {
        const registered = await registerMarkerConfigImage(map, 'osm-picto-pin-mc', markerConfig, color)
        if (registered) {
          imageId = registered.imageId
          iconAnchor = registered.anchor
          resolvedColor = registered.color
        }
      }

      if (!imageId) {
        imageId = `osm-picto-pin-${color.replace('#', '')}`
        if (!map.hasImage(imageId)) {
          try {
            const svg = await MarkerGeneratorService.generateMarker(color)
            const img = await MarkerGeneratorService.createImageFromSvg(svg)
            if (!map.hasImage(imageId)) map.addImage(imageId, img)
          } catch (error) {
            console.error(`[map-styles] failed to generate picto pin for color "${color}":`, error)
            continue
          }
        }
      }

      const mutableLayer = layer as unknown as Record<string, unknown>
      mutableLayer.type = 'symbol'
      delete mutableLayer.paint
      mutableLayer.layout = {
        ...(mutableLayer.layout as Record<string, unknown> | undefined),
        'icon-image': imageId,
        'icon-size': iconAnchor === 'center' ? 0.65 : 0.5,
        'icon-anchor': iconAnchor,
        'icon-allow-overlap': true,
      }
      // Stashed so a classification selection (right panel) can later restore this exact
      // "no category selected" appearance — see OsmLayerHandler.applyClassificationColor.
      mutableLayer.metadata = {
        ...(mutableLayer.metadata as Record<string, unknown> | undefined),
        'sigcom:defaultIconImageId': imageId,
        'sigcom:defaultIconColor': resolvedColor,
        'sigcom:defaultIconAnchor': iconAnchor,
      }
    }
  }

  /**
   * Applies an OSM style to the map with prefixed source and layer ids (avoids clashes when multiple OSM layers are on).
   * Returns the prefixed source and layer ids so the caller can remove them later.
   */
  static async applyOsmStyleToMap(
    myMap: InstanceType<typeof Map> | undefined,
    styleId: string,
    cmnCode?: string | null,
    markerConfig?: TypeOuvrageMarkerConfig | null,
  ): Promise<{ sourceIds: string[]; layerIds: string[] } | null> {
    const style = await this.loadStyleFromMapStyles(styleId, cmnCode)
    if (!style?.sources || Object.keys(style.sources).length === 0 || !style.layers?.length)
      return null
    const prefixed = this.cloneStyleWithPrefix(style, styleId)

    const mapInstance = await this.waitForMaplibreMap(myMap, 5_000)
    if (mapInstance) {
      await this.convertCirclePointLayersToPictoPins(mapInstance, prefixed, markerConfig)
    }

    const applied = await this.applyStyleObjectToMap(myMap, prefixed)
    if (!applied) return null

    // Register custom icon images (uploaded via admin) so symbol layers that
    // reference a sigcom_map_icon_* image id are rendered correctly in the geoportail.
    if (mapInstance) {
      await loadCustomIconImagesForMap(mapInstance, prefixed as unknown as MapLibreStyle)
    }

    return this.getAppliedOsmStyleIds(prefixed)
  }

  /**
   * Removes an OSM style from the map (layers first, then sources).
   * Only removes a source if no remaining layers reference it.
   * Call with the ids returned from applyOsmStyleToMap.
   */
  static removeOsmStyleFromMap(
    myMap: InstanceType<typeof Map> | undefined,
    layerIds: string[],
    sourceIds: string[],
  ): void {
    const map = myMap?.map
    if (!map) return
    const layerList = [...layerIds].reverse()
    for (const layerId of layerList) {
      if (map.getLayer(layerId)) map.removeLayer(layerId)
    }
    for (const sourceId of sourceIds) {
      if (!map.getSource(sourceId)) continue
      const remainingLayers = map
        .getStyle()
        .layers.filter((l) => 'source' in l && l.source === sourceId)
      if (remainingLayers.length === 0) {
        map.removeSource(sourceId)
      }
    }
  }

  /**
   * Ajoute uniquement les sources d'un style sans ajouter les layers
   * Utile pour charger les données sans les afficher immédiatement
   */
  static async addStyleSourcesToMap(
    myMap: InstanceType<typeof Map> | undefined,
    styleId: string,
    loadFromMartin: boolean = false,
  ): Promise<boolean> {
    if (!myMap?.map) {
      console.error("La carte n'est pas initialisée")
      return false
    }

    const map = myMap.map

    // Attendre que la carte soit prête
    if (!map.loaded()) {
      await new Promise<void>((resolve) => {
        map.once('load', () => resolve())
      })
    }

    // Charger le style
    const style = loadFromMartin
      ? await this.loadStyleFromMartin(styleId)
      : await this.loadStyleFromFile(styleId)

    if (!style || !style.sources) {
      console.error(`Impossible de charger les sources du style ${styleId}`)
      return false
    }

    // Ajouter uniquement les sources
    Object.keys(style.sources).forEach((sourceId) => {
      const source = style.sources![sourceId]
      if (source && !map.getSource(sourceId)) {
        try {
          map.addSource(sourceId, source)
        } catch (error) {
          console.warn(`La source ${sourceId} existe déjà ou ne peut pas être ajoutée:`, error)
        }
      }
    })

    return true
  }

  /**
   * Ajoute uniquement les layers d'un style (les sources doivent déjà exister)
   */
  static async addStyleLayersToMap(
    myMap: InstanceType<typeof Map> | undefined,
    styleId: string,
    loadFromMartin: boolean = false,
  ): Promise<boolean> {
    if (!myMap?.map) {
      console.error("La carte n'est pas initialisée")
      return false
    }

    const map = myMap.map

    // Attendre que la carte soit prête
    if (!map.loaded()) {
      await new Promise<void>((resolve) => {
        map.once('load', () => resolve())
      })
    }

    // Charger le style
    const style = loadFromMartin
      ? await this.loadStyleFromMartin(styleId)
      : await this.loadStyleFromFile(styleId)

    if (!style || !style.layers) {
      console.error(`Impossible de charger les layers du style ${styleId}`)
      return false
    }

    // Ajouter uniquement les layers
    style.layers.forEach((layer) => {
      if (!map.getLayer(layer.id)) {
        try {
          map.addLayer(layer)
        } catch (error) {
          console.warn(`Le layer ${layer.id} ne peut pas être ajouté:`, error)
        }
      }
    })

    return true
  }

  /**
   * Supprime un style de la carte (supprime les sources et layers associés)
   */
  static removeStyleFromMap(myMap: InstanceType<typeof Map> | undefined, styleId: string): void {
    if (!myMap?.map) {
      return
    }

    const map = myMap.map

    // Charger le style pour connaître les sources et layers à supprimer
    this.loadStyleFromFile(styleId).then((style) => {
      if (!style) {
        return
      }

      // Supprimer les layers
      if (style.layers) {
        style.layers.forEach((layer) => {
          if (map.getLayer(layer.id)) {
            map.removeLayer(layer.id)
          }
        })
      }

      // Supprimer les sources
      if (style.sources) {
        Object.keys(style.sources).forEach((sourceId) => {
          if (map.getSource(sourceId)) {
            map.removeSource(sourceId)
          }
        })
      }
    })
  }

  /**
   * Vide le cache des styles
   */
  static clearCache(): void {
    this.styleCache.clear()
  }

  static clearCacheForId(styleId: string): void {
    this.styleCache.delete(styleId)
  }

  static getCachedStyle(styleId: string): StyleSpecification | undefined {
    return this.styleCache.get(styleId)
  }
}
